// 📄 facturas.tsx
import React from 'react';
import { CheckCircle2, Circle, ArrowRight, Save, RefreshCw, AlertTriangle } from 'lucide-react';
import { useFacturas } from './useFacturas.ts';
import { useChecklistIncidentAlerts } from './useChecklistIncidentAlerts.ts';
import { useProject } from '../../../../context/ProjectContext.tsx';
import { buildChecklistStepKey } from '../../../../data/commerceChecklistCatalog.ts';
import ChecklistIncidentBanner from '../../../Proyectos/components/ChecklistIncidentBanner.tsx';
import CustomDevelopmentSection from '../../../../components/integracion/CustomDevelopmentSection.tsx';
import { emptyCustomDevelopment } from '../../../../types/customDevelopment.ts';
import EnvironmentTabs from '../../../../components/integracion/EnvironmentTabs.tsx';
import TransportConfirmationPanel from '../../../../components/integracion/TransportConfirmationPanel.tsx';
import { emptyTransportConfirmation } from '../../../../types/transportConfirmation.ts';

import { StepInvoiceParams } from './components/StepInvoiceParams.tsx';
import { StepInvoiceTax } from './components/StepInvoiceTax.tsx';
import { StepInvoicePosting } from './components/StepInvoicePosting.tsx';
import { StepInvoiceExits } from './components/StepInvoiceExits.tsx';

const INTEGRATION_STEPS = [
  { id: 'invoice_params', title: '1. Maintain Parameters', desc: 'INVOICE_ENABLED, INVOICE_PARK_NO_ACC, INV_HDR_PRK_NOSES_POGRX_ENB' },
  { id: 'invoice_tax', title: '2. Company Code & Tax Codes', desc: 'Partner EDI y conversión de tasas de impuesto' },
  { id: 'invoice_posting', title: '3. Program Parameters & Posting Keys', desc: 'Claves de contabilización y tipo de documento RE' },
  { id: 'invoice_exits', title: '4. Customer Exits & BTE Linkages', desc: 'Enhancement MRMH0002 y Event Linkage en SWE2' }
];

export default function FacturasConfig() {
  const { esCliente, projectId } = useProject();
  const {
    activeStep,
    setActiveStep,
    completedSteps,
    formData,
    handleFieldChange,
    requiresOTMap,
    setRequiresOTMap,
    otNumberMap,
    setOtNumberMap,
    customDevMap,
    setCustomDevMap,
    qasTransportMap,
    setQasTransportMap,
    prdTransportMap,
    setPrdTransportMap,
    qasManualMap,
    prdManualMap,
    confirmingTransport,
    handleConfirmTransport,
    validationError,
    setValidationError,
    saveStatus,
    loadingData,
    handleSave,
    selectedEnv,
    handleEnvChange
  } = useFacturas();

  const incidentAlerts = useChecklistIncidentAlerts(projectId, selectedEnv === 'PRD');
  const activeStepIncidente = incidentAlerts[buildChecklistStepKey('invoice', activeStep)];

  if (loadingData) {
    return (
      <div className="p-8 text-center text-gray-400 flex flex-col items-center justify-center space-y-3 h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <p className="text-sm">Sincronizando configuración de Facturas con OneHub Cloud...</p>
      </div>
    );
  }

  const isDevView = selectedEnv === 'DEV';
  const stepRequiresOT = requiresOTMap[activeStep] || false;
  const showTransportPanel = !isDevView && stepRequiresOT;
  const showEnvEditableSave = !isDevView && !stepRequiresOT;

  return (
    <div className="p-2 text-white max-w-6xl mx-auto space-y-6">
      {validationError && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-xl flex items-center gap-3 text-sm animate-fadeIn">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span>{validationError}</span>
        </div>
      )}

      {activeStepIncidente && projectId && (
        <ChecklistIncidentBanner projectId={projectId} incidente={activeStepIncidente} />
      )}

      {saveStatus === 'success' && (
        <div className="bg-green-500/10 border border-green-500/30 text-green-400 p-4 rounded-xl text-sm animate-fadeIn">
          🎉 ¡Configuración de Facturas guardada con éxito!
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 bg-white/5 border border-white/10 rounded-2xl p-4 h-fit space-y-3">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider px-2 mb-2">Progreso de Integración</h3>
          <div className="space-y-2">
            {INTEGRATION_STEPS.map((step) => {
              const isCompleted = completedSteps.includes(step.id);
              const isActive = activeStep === step.id;

              return (
                <div
                  key={step.id}
                  onClick={() => { setActiveStep(step.id); setValidationError(null); }}
                  className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all border ${
                    isActive ? 'bg-blue-500/10 border-blue-500 text-white' : 'bg-transparent border-transparent hover:bg-white/5 text-gray-300'
                  }`}
                >
                  <div className="mt-0.5 text-blue-400">
                    {isCompleted ? <CheckCircle2 className="w-5 h-5 fill-blue-500 text-slate-900" /> : <Circle className="w-5 h-5 text-gray-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${isCompleted && !isActive ? 'line-through text-gray-500' : ''}`}>{step.title}</p>
                    <p className="text-xs text-gray-400 truncate mt-0.5">{step.desc}</p>
                  </div>
                  {isActive && <ArrowRight className="w-4 h-4 text-blue-400 self-center" />}
                </div>
              );
            })}
          </div>

          <div className="pt-2 px-2">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>Pasos completados</span>
              <span>{completedSteps.length} de {INTEGRATION_STEPS.length}</span>
            </div>
            <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden">
              <div className="bg-blue-500 h-full transition-all duration-300" style={{ width: `${(completedSteps.length / INTEGRATION_STEPS.length) * 100}%` }} />
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col min-h-[450px] min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-white/10 pb-4 mb-6 gap-3">
            <div>
              <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">Configuración Activa</span>
              <h4 className="text-sm text-gray-400">
                {isDevView ? 'Estás editando' : showTransportPanel ? 'Estás consultando (heredado de DEV)' : 'Estás editando (independiente por ambiente)'} los parámetros de Facturas en:
              </h4>
            </div>
            <EnvironmentTabs
              selectedEnv={selectedEnv}
              onChange={handleEnvChange}
              devConfigured={completedSteps.includes(activeStep)}
              stepRequiresOT={stepRequiresOT}
              qas={(stepRequiresOT ? qasTransportMap : qasManualMap)[activeStep] || emptyTransportConfirmation()}
              prd={(stepRequiresOT ? prdTransportMap : prdManualMap)[activeStep] || emptyTransportConfirmation()}
            />
          </div>

          <div className="flex-1 flex flex-col justify-between">
            <fieldset disabled={esCliente || showTransportPanel}>
              {activeStep === 'invoice_params' && (
                <StepInvoiceParams
                  invoiceEnabled={formData.invoiceEnabled}
                  invoiceParkNoAcc={formData.invoiceParkNoAcc}
                  invHdrPrkNosesPogrxEnb={formData.invHdrPrkNosesPogrxEnb}
                  onChange={handleFieldChange}
                />
              )}
              {activeStep === 'invoice_tax' && (
                <StepInvoiceTax
                  companyCodeEdi={formData.companyCodeEdi}
                  taxMappingNotes={formData.taxMappingNotes}
                  onChange={handleFieldChange}
                  isInvalid={!!validationError}
                />
              )}
              {activeStep === 'invoice_posting' && (
                <StepInvoicePosting
                  glDrPostingKey={formData.glDrPostingKey}
                  glCrPostingKey={formData.glCrPostingKey}
                  vendDrPostingKey={formData.vendDrPostingKey}
                  vendCrPostingKey={formData.vendCrPostingKey}
                  invoiceDocType={formData.invoiceDocType}
                  onChange={handleFieldChange}
                  isInvalid={!!validationError}
                />
              )}
              {activeStep === 'invoice_exits' && (
                <StepInvoiceExits
                  exitMRMH0002Active={formData.exitMRMH0002Active}
                  swe2ObjectType={formData.swe2ObjectType}
                  swe2Event={formData.swe2Event}
                  swe2ReceiverFunction={formData.swe2ReceiverFunction}
                  onChange={handleFieldChange}
                  isInvalid={!!validationError}
                />
              )}
            </fieldset>

            {isDevView && (
              <fieldset disabled={esCliente} className="border-t border-white/10 pt-4 mt-6 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={requiresOTMap[activeStep] || false}
                        onChange={(e) => {
                          setRequiresOTMap({ ...requiresOTMap, [activeStep]: e.target.checked });
                          setValidationError(null);
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-zinc-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-blue-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                    </label>
                    <div>
                      <span className="text-xs font-medium block">¿Requiere Orden de Transporte (OT)?</span>
                      <span className="text-[10px] text-gray-400">Actívalo si estás guardando cambios en un mandante de SAP configurable.</span>
                    </div>
                  </div>

                  {(requiresOTMap[activeStep] || false) && (
                    <div className="flex-1 max-w-xs animate-fadeIn">
                      <input
                        type="text"
                        value={otNumberMap[activeStep] || ''}
                        onChange={(e) => setOtNumberMap({ ...otNumberMap, [activeStep]: e.target.value.toUpperCase() })}
                        maxLength={10}
                        placeholder="ej. DEVK900123"
                        className="w-full bg-zinc-800 border border-blue-500/30 text-blue-400 rounded-xl px-3 py-2 text-sm font-mono uppercase tracking-wider text-center focus:outline-none"
                      />
                    </div>
                  )}
                </div>

                <CustomDevelopmentSection
                  value={customDevMap[activeStep] || emptyCustomDevelopment()}
                  onChange={(data) => setCustomDevMap({ ...customDevMap, [activeStep]: data })}
                />

                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Comentarios / Bitácora del Consultor Técnico</label>
                  <textarea
                    value={formData.comments}
                    onChange={(e) => handleFieldChange('comments', e.target.value)}
                    placeholder="Aclaraciones sobre este paso en el ambiente de DEV..."
                    rows={2}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-2.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none transition-all"
                  />
                </div>

                {!esCliente && (
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={handleSave}
                      disabled={saveStatus === 'saving'}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-lg text-slate-950 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600"
                    >
                      <Save className="w-4 h-4" />
                      {saveStatus === 'saving' ? 'Guardando...' : 'Guardar Ajustes en DEV'}
                    </button>
                  </div>
                )}
              </fieldset>
            )}

            {showEnvEditableSave && (
              <fieldset disabled={esCliente} className="border-t border-white/10 pt-4 mt-6 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Comentarios / Bitácora del Consultor Técnico ({selectedEnv})</label>
                  <textarea
                    value={formData.comments}
                    onChange={(e) => handleFieldChange('comments', e.target.value)}
                    placeholder={`Aclaraciones sobre este paso en el ambiente de ${selectedEnv}...`}
                    rows={2}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-2.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none transition-all"
                  />
                </div>

                {!esCliente && (
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={handleSave}
                      disabled={saveStatus === 'saving'}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-lg text-slate-950 ${
                        selectedEnv === 'PRD' ? 'bg-red-500 hover:bg-red-600' : 'bg-orange-500 hover:bg-orange-600'
                      } disabled:bg-gray-600`}
                    >
                      <Save className="w-4 h-4" />
                      {saveStatus === 'saving' ? 'Guardando...' : `Guardar Ajustes en ${selectedEnv}`}
                    </button>
                  </div>
                )}
              </fieldset>
            )}

            {showTransportPanel && (
              <fieldset disabled={esCliente}>
                <TransportConfirmationPanel
                  env={selectedEnv as 'QAS' | 'PRD'}
                  value={(selectedEnv === 'QAS' ? qasTransportMap : prdTransportMap)[activeStep] || emptyTransportConfirmation()}
                  onChange={(data) => {
                    if (selectedEnv === 'QAS') setQasTransportMap({ ...qasTransportMap, [activeStep]: data });
                    else setPrdTransportMap({ ...prdTransportMap, [activeStep]: data });
                  }}
                  onConfirm={() => handleConfirmTransport(selectedEnv as 'QAS' | 'PRD')}
                  saving={confirmingTransport}
                />
              </fieldset>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
